import { Component, OnDestroy, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { LanguageHandlerService } from 'src/app/services/language-handler.service';
import { APIConfigService } from 'src/app/services/api-config.service';
import { iterateAPIConfig } from 'src/app/helpers/api-resp-helper';
import { states } from 'src/app/constants/states';
import { searchIconSVG } from 'src/app/constants/svgs/search-icon';
import { cacheKeys } from 'src/app/constants/cache-keys';
import { Service, ServiceFilter } from '../../models/all-services.model';
import { PageAPIResp } from 'src/app/models/api-config.model';
import { CallAPIService } from 'src/app/services/call-api.service';
import { StoreService } from 'src/app/services/store.service';

@Component({
  selector: 'app-services',
  templateUrl: './services.component.html',
  styleUrls: ['./services.component.scss'],
})
export class ServicesComponent implements OnInit, OnDestroy {
  allAPIsResp: PageAPIResp = {};
  filteredServices!: Service[];
  filterConfig!: ServiceFilter;
  cacheKeys = cacheKeys;
  states:any = states;
  searchIconSVG = searchIconSVG;
  private title = 'UMANG Services';
  private description =
    'UMANG provides a single platform for all Indian Citizens to access pan India e-Gov services ranging from Central to Local Government bodies includes EPFO, My Pan, AICTE, SSC, Bharat Gas, NSP and many more.';
  private langChangeSubs!: Subscription;
  serviceStateList: any[] = [];
  dbtServices!: Service[];

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private activatedRoute: ActivatedRoute,
    private apiConfigService: APIConfigService,
    private langHandlerService: LanguageHandlerService,
    private callApiService: CallAPIService,
    private storeService:StoreService
  ) {}

  ngOnInit() {
    this.setTitleAndMeta();
    this.initServiceFilter();
    iterateAPIConfig(
      this.apiConfigService.getAllServicesPageConfig(),
      this.allAPIsResp,
      this.actionsOnResp.bind(this)
    );
    this.addLangChangeListener();
  }

  private setTitleAndMeta() {
    this.titleService.setTitle(this.title);
    this.metaService.updateTag({
      name: 'description',
      content: this.description,
    });
  }

  private setKeywords(keywords: string) {
    this.metaService.updateTag({
      name: 'keywords',
      content: keywords || '',
    });
  }

  private initServiceFilter() {
    this.filterConfig = {
      serviceName: '',
      state: this.activatedRoute.snapshot.queryParams['stateId'] || '0',
      category: this.activatedRoute.snapshot.queryParams['category'],
    };

    if(this.activatedRoute.snapshot.queryParams['category'] === '1000'){
      this.onEditCategory(this.activatedRoute.snapshot.queryParams['category']);
    }
  }

  /**
   * Method that filter services & update meta-keywords when response comes from API.
   */
  private actionsOnResp() {
    // Filter services only when response comes from "allservices" APIs
    if (
      cacheKeys.allServices in this.allAPIsResp &&
      !this.allAPIsResp[cacheKeys.allServices]?.isLoading &&
      !this.filteredServices?.length
    ) {
      // Get comma separated service names
      const allServicesStr = (<Service[]>(
        this.allAPIsResp[cacheKeys.allServices].data
      ))
        ?.map((service) => service.serviceName)
        .join(', ');
      this.setKeywords(allServicesStr);
      this.onFilterServices();
    }
  }

  private addLangChangeListener() {
    this.langChangeSubs = this.langHandlerService.onLangChange.subscribe(() => {
      this.filteredServices = [];

      iterateAPIConfig(
        this.apiConfigService.getAllServicesPageConfig(),
        this.allAPIsResp,
        this.actionsOnResp.bind(this)
      );
    });
  }

  get categories() {
    return this.allAPIsResp[cacheKeys.categories]?.data;
  }

  get isServicesLoading() {
    return this.allAPIsResp[cacheKeys.allServices]?.isLoading;
  }

  get allServices() {
    return this.allAPIsResp[cacheKeys.allServices]?.data;
  }

  onEditCategory(categoryId: string) {
    const isCtgSelected = this.filterConfig.category === categoryId;
      this.filterConfig.category = isCtgSelected ? '' : categoryId;
    if(categoryId === '1000'){
      this.filterConfig.category = categoryId;
      const payload = {
        lang: 'en',
        status: 'active'
      }
      this.callApiService.fetchDBTSchemeList(payload).subscribe((res) => {
        this.filteredServices = res?.pd.map((item:any) => {
          return {
            serviceName: item.schemeName,
            categoryName: item.departmentName,
            image: "https://media.umangapp.in/app/ico/service/NationalEmblem23.png",
            type:'scheme',
            categoryId: categoryId,
            schemeId: item.schemeId
          }
        })
        this.dbtServices = [...this.filteredServices];
      });
    }
    else{
      
      this.onFilterServices();
    }
  }

  onFilterServices() {
    if (!this.allServices?.length) {
      return;
    }

    

    const checkForOtherState = (stateId: string, otherState: string) => {
      if (!otherState) {
        return false;
      }

      return otherState.split('|').includes(stateId);
    };
    

    const {
      serviceName: filterName,
      state: filterState,
      category: filterCategory,
    } = this.filterConfig;
    
    if(filterCategory === '1000'){
      this.filteredServices = this.dbtServices.filter(
        (res:any) => {
          return res.serviceName.toLowerCase().includes(filterName.toLowerCase()) ||
          res.categoryName.toLowerCase().includes(filterName.toLowerCase())
        }
      )
    }
    else{
    this.filteredServices = this.allServices.filter(
      ({ serviceName, state, otherState, categoryId }: Service) => {
        const isNameMatch = serviceName
          ?.toLowerCase()
          .includes(filterName?.toLowerCase());

        // filterState is converted to a number bcz we don't want filtering on "0" (All) states
        const isStateMatch = +filterState
          ? state === filterState || checkForOtherState(filterState, otherState)
          : true;
        const isCategoryMatch = !!filterCategory
          ? categoryId === filterCategory
          : true;

        return isNameMatch && isStateMatch && isCategoryMatch;
      }
    );
    const set = new Set();
    set.add('0')
    this.allServices.forEach((service:any) => {
      if(service.depttype !== 'U'){
        set.add(service.state);
      if (service.otherState) {
        const st = service.otherState.split('|');
        st.forEach((s:any) => {
          if (s != '') {
            set.add(s);
          }
        });
      }
      }
    });
    const stateId = [...set];
    this.states = this.states.map((elm:any)=>{
      return {
        stateId: elm.stateId,
        isUpcoming:!stateId.includes(elm.stateId),
        stateName: elm?.stateName
      }
    });
    const [nonUpcomingList, upcomingList] = [
      this.states.filter((elm:any) => !elm.isUpcoming).sort((a:any, b:any) => a.stateName.localeCompare(b.stateName)),
      this.states.filter((elm:any) => elm.isUpcoming)
    ];
    this.states = [...nonUpcomingList, ...upcomingList ];
    console.log("this.states::::",this.states)
    const centralStateIndex = this.states.findIndex(state => state.stateId === "99");
    if (centralStateIndex > -1) {
      const [centralState] = this.states.splice(centralStateIndex, 1);
      // Insert it at the second position (index 1)
      this.states.splice(1, 0, centralState);
  }
    // const list = this.filteredServices.filter(c => c.depttype != 'U');
     

    // this.serviceStateList = list.map((ele:any)=>ele?.state || ele.otherState);
    this.serviceStateList = stateId;
    }
  }

  ngOnDestroy() {
    this.langChangeSubs.unsubscribe();
  }
}
